import React, { useEffect, useState } from 'react';
import Modal from '../Modal';
import Button from '../../ui/buttons/Button';
import Input from '../../ui/inputs/Input';
import CurrencyInput from '../../ui/inputs/CurrencyInput';
import CustomCheckbox from '../../ui/checkboxes/CustomCheckbox';
import Select from '../../ui/selects/Select';
import Textarea from '../../ui/inputs/Textarea';
import { ShieldCheck, X } from 'lucide-react';
import { fetchWithCsrf } from '../../../utils/fetchWithCsrf';
import { snakecaseKeys } from '../../../utils/snakecaseKeys';
import { camelizeKeys } from '../../../utils/camelizeKeys';
import { useToast } from '../../ui/toast/ToastContext';
import CnpjInput from '../../ui/inputs/CnpjInput';
import SusepInput from '../../ui/inputs/SusepInput';

const RetirementModal = ({ clientId, retirementId = null, scope, onClose, onSuccess }) => {
  const isEdit = !!retirementId;
  const { addToast } = useToast();

  const [form, setForm] = useState({
    company: '',
    planName: '',
    companyCnpj: '',
    investmentFundCnpj: '',
    susepProcessNumber: '',
    planType: '',
    taxRegime: '',
    fundType: '',
    totalValue: '',
    notes: '',
  });

  // Carrega dados do seguro (modo edição)
  useEffect(() => {
    if (isEdit) {
      fetch(`/app/planner/client/${clientId}/dashboard/properties/retirements/${retirementId}?scope=${scope}`)
        .then((res) => res.json())
        .then((data) => setForm(camelizeKeys(data)))
        .catch((err) => {
          console.error('Erro ao carregar previdencia', err);
          addToast({ type: 'error', message: 'Erro ao carregar dados da previdencia' });
        });
    }
  }, [isEdit, clientId, retirementId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckbox = (name) => {
    setForm((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = snakecaseKeys({ retirementProperty: form });

    const url = isEdit
      ? `/app/planner/client/${clientId}/dashboard/properties/retirements/${retirementId}?scope=${scope}`
      : `/app/planner/client/${clientId}/dashboard/properties/retirements?scope=${scope}`;

    const method = isEdit ? 'PATCH' : 'POST';
    const successMsg = isEdit ? 'Previdência atualizada com sucesso!' : 'Previdência cadastrada com sucesso!';

    try {
      const res = await fetchWithCsrf(url, {
        method,
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || 'Erro ao salvar previdência');
      }

      onClose();
      addToast({ type: 'success', message: successMsg });
      onSuccess();
    } catch (err) {
      addToast({ type: 'error', message: err.message });
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="p-10">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3 text-blueDarker font-jost text-2xl font-semibold">
            <ShieldCheck size={24} className="text-tealMedium" strokeWidth={1.25} />
            {isEdit ? 'Editar Previdência' : 'Cadastrar Previdência'}
          </div>
          <button onClick={onClose}>
            <X size={24} className="text-tealBase hover:opacity-80" strokeWidth={1.25} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 font-jost text-sm">
          <Input name="company" label="Seguradora" value={form.company} onChange={handleChange} />
          <Input name="planName" label="Nome do plano" value={form.planName} onChange={handleChange} />
          <CnpjInput name="companyCnpj" label="CNPJ da seguradora" value={form.companyCnpj} onChange={handleChange} />
          <CnpjInput
            name="investmentFundCnpj"
            label="CNPJ do fundo"
            value={form.investmentFundCnpj}
            onChange={handleChange}
          />
          <SusepInput
            name="susepProcessNumber"
            label="Número do processo SUSEP"
            value={form.susepProcessNumber}
            onChange={handleChange}
          />
          <Select
            name="planType"
            label="Tipo de plano"
            value={form.planType}
            onChange={handleChange}
            options={[
              { value: 'PGBL', label: 'PGBL' },
              { value: 'VGBL', label: 'VGBL' },
            ]}
          />
          <Select
            name="taxRegime"
            label="Regime de tributação"
            value={form.taxRegime}
            onChange={handleChange}
            options={[
              { value: 'Progressivo', label: 'Progressivo' },
              { value: 'Regressivo', label: 'Regressivo' },
            ]}
          />
          <Select
            name="fundType"
            label="Tipo de fundo"
            value={form.fundType}
            onChange={handleChange}
            options={[
              { value: 'Aberto', label: 'Aberto' },
              { value: 'Fechado', label: 'Fechado' },
            ]}
          />
          <CurrencyInput name="totalValue" label="Valor total" value={form.totalValue} onChange={handleChange} />

          <Textarea
            name="notes"
            label="Observações"
            value={form.notes}
            onChange={handleChange}
            className="md:col-span-2"
            rows={4}
          />

          <div className="md:col-span-2 flex justify-end items-center mt-1">
            <Button type="submit">{isEdit ? 'SALVAR ALTERAÇÕES' : 'CADASTRAR PREVIDÊNCIA'}</Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default RetirementModal;
